import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getAgents,
  getAgentById,
  createAgent,
  updateAgent,
  deleteAgent,
} from "../api/client";

/**
 * Hook para listar e gerenciar agentes de IA.
 *
 * - Lista paginada via GET /agents
 * - Criação via POST /agents
 * - Atualização via PUT /agents/:id
 * - Exclusão via DELETE /agents/:id
 */
export const useAgents = (page = 1, limit = 20, search = "", options = {}) => {
  const { enabled = true, staleTime = 5_000 } = options;
  const queryClient = useQueryClient();

  const listQuery = useQuery(
    ["agents", page, limit, search],
    () => getAgents({ page, limit, search }),
    {
      enabled: Boolean(enabled),
      keepPreviousData: true,
      staleTime,
      refetchOnWindowFocus: false,
      select: (res) => {
        const raw = res?.data ?? res;
        const list = Array.isArray(raw) ? raw : raw?.items ?? raw?.agents ?? [];
        const pagination = res?.pagination || {
          total: raw?.total ?? list.length,
          page,
          limit,
        };
        return { agents: list, pagination };
      },
    }
  );

  const createMutation = useMutation(
    (payload) => createAgent(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("agents");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => updateAgent(id, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries("agents");
        queryClient.invalidateQueries("filter-options");
        if (variables?.id) {
          queryClient.invalidateQueries(["agent", variables.id]);
        }
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => deleteAgent(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("agents");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  const getById = async (id) => {
    if (!id) return null;
    const res = await getAgentById(id);
    return res?.data ?? res ?? null;
  };

  return {
    ...listQuery,
    createMutation,
    updateMutation,
    deleteMutation,
    getById,
  };
};

